import { InputDescription, InputName } from "@components-common/forms";
import { application } from "../../../_runtime/bootstrap";
import { useField, useFormikContext } from "formik";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@hooks";
import { OverrideValueTable } from "../elements/OverrideValueTable";
import { useLoading } from "../../../components-common/_shared/loading/use-loading.hook";
import { Loader } from "taltech-styleguide";
import { mapSpaceParameters } from "./_helper";
import {
  handleTypeParameters,
  handleTypeParametersRequests,
} from "../elements/component-type-creation/_helpers";
import { clone } from "ramda";
import { useUserContext } from "../../../_runtime/iam/session.context";
import { roleMapper } from "../../../_runtime/iam/session.provider";

/**
 * Functional Component: SpaceFunctionFormContent
 *
 * This component renders the content of the space function form, which includes input fields for name and description.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the input fields for the space function form.
 */
export const SpaceFunctionFormContent: FC<any> = () => {
  const [fieldTypeParameters] = useField("typeParameters");
  return (
    <>
      <InputName elementClass="required" />
      <InputDescription />
      <div className="mt-3">
        <h1>Space Parameters</h1>
        <OverrideValueTable tableItems={fieldTypeParameters.value} />
      </div>
    </>
  );
};

/**
 * Functional Component: SpaceFunctionForm
 *
 * This component manages the submission of the space function form. It handles both the creation and updating of space functions.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.id - The ID of the form, used to determine if the form is for creation or editing.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the space function form.
 */
export const SpaceFunctionForm: FC<any> = ({ ...props }) => {
  const nav = useNavigate();
  const formik = useFormikContext();
  const notifications = useNotifications();
  const [loading, setLoading] = useLoading(true);
  const [overridenParameters, setOverridenParameters] = useState<any>([]);
  const { userId, userRole } = useUserContext();

  useEffect(() => {
    mapSpaceParameters(
      formik,
      setLoading,
      setOverridenParameters,
      props.id !== "form_spaces_create"
    );
  }, []);

  useEffect(() => {
    if (formik.isSubmitting) {
      const values: any = formik.values;
      let clonnedValues = clone(values);

      if (props.id === "form_spaces_create") {
        delete clonnedValues["typeParameters"];
        clonnedValues = {
          ...clonnedValues,
          sourceId: userId,
          sourceType: userRole === roleMapper.Student ? "USER" : "SYSTEM",
        };
        application
          .getHttpClient()
          .post("/buildingms/space-functions", clonnedValues)
          .then((res: any) => {
            const typeParameters = handleTypeParameters(
              res.body.id as string,
              values,
              overridenParameters
            );
            handleTypeParametersRequests(typeParameters);
            nav(-1);
            formik.setSubmitting(false);
          })
          .catch((error: any) => {
            notifications.error({ title: error.response.data.detail });
            formik.setSubmitting(false);
          });
      } else {
        const typeParameters = handleTypeParameters(
          values.id as string,
          values,
          overridenParameters
        );
        delete clonnedValues["typeParameters"];
        application
          .getHttpClient()
          .put(`/buildingms/space-functions/${values.id}`, clonnedValues)
          .then((res: any) => {
            handleTypeParametersRequests(typeParameters);
            nav(-1);
            formik.setSubmitting(false);
          })
          .catch((error: any) => {
            notifications.error({ title: error.response.data.detail });
            formik.setSubmitting(false);
          });
      }
    }
  }, [formik.isSubmitting]);

  return loading ? <Loader overlay /> : <SpaceFunctionFormContent />;
};
